import React, { useState } from 'react';
import axios from 'axios';
import { MagnifyingGlassIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';

interface Paper {
  title: string;
  authors: string[];
  abstract: string;
  year: number;
  citations: number;
  url: string;
}

interface SearchFilters {
  year?: number;
  author?: string;
  field?: string;
}

const Search: React.FC = () => {
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({});
  const [papers, setPapers] = useState<Paper[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fullContent, setFullContent] = useState('');
  const [showFullContent, setShowFullContent] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Use full URL with localhost or your deployed API
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      console.log('Search request:', { query, filters, apiUrl });
      const response = await axios.post(`${apiUrl}/api/search`, {
        query,
        filters
      });
      console.log('Search response:', response.data);
      
      if (response.data) {
        setPapers(response.data.papers || []);
        setFullContent(response.data.fullContent || '');
      } else {
        setPapers([]);
        setFullContent('');
        setError('Invalid response format from server');
      }
    } catch (error: any) {
      console.error('Search failed:', error);
      // Show more detailed error information
      setPapers([]);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to perform search';
      setError(`${errorMessage}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <form onSubmit={handleSearch} className="mb-8">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for papers, authors, or topics..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <MagnifyingGlassIcon className="h-5 w-5 absolute right-3 top-2.5 text-gray-400" />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <AdjustmentsHorizontalIcon className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 p-4 bg-white rounded-lg shadow">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Year</label>
                <input
                  type="number"
                  value={filters.year || ''}
                  onChange={(e) => setFilters({ ...filters, year: parseInt(e.target.value) || undefined })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Author</label>
                <input
                  type="text"
                  value={filters.author || ''}
                  onChange={(e) => setFilters({ ...filters, author: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Field</label>
                <input
                  type="text"
                  value={filters.field || ''}
                  onChange={(e) => setFilters({ ...filters, field: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        )}
      </form>

      {error && (
        <div className="text-red-600 mb-4 text-center">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      ) : papers.length > 0 ? (
        <div>
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xl font-bold">Search Results</h2>
            <button
              onClick={() => setShowFullContent(!showFullContent)}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md"
            >
              {showFullContent ? "Show Individual Papers" : "Show Full Content"}
            </button>
          </div>
          
          {showFullContent ? (
            <div className="bg-white shadow-md rounded-lg p-6 mb-6 prose max-w-none">
              <ReactMarkdown>{fullContent}</ReactMarkdown>
            </div>
          ) : (
            <div>
              {papers.map((paper, index) => (
                <div key={index} className="bg-white shadow-md rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold">{paper.title}</h3>
                  <div className="text-sm text-gray-600 mt-1 mb-2">
                    {paper.authors.join(', ')} • {paper.year}
                  </div>
                  <p className="text-gray-800">{paper.abstract}</p>
                  <div className="mt-4 text-sm text-gray-500">
                    {paper.citations} citations
                  </div>
                  <div className="mt-2">
                    <a 
                      href={paper.url} 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm"
                    >
                      View source
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-10">
          <p className="text-gray-600">
            No papers found. Try a different search query.
          </p>
        </div>
      )}
    </div>
  );
};

export default Search;